<script setup>
import { onMounted, ref } from "vue";
import { RouterView } from "vue-router";
import { useFavicon } from "@vueuse/core";
import { useIdentifyUser } from "@/composables/useIdentifyUser";
import { useAuthStore } from "@/stores/auth";

import { logAppBootTime, logEvent } from "@/utils/tracking";
import trackedEvents from "@/utils/trackedEvents";

import NotificationToast from "@/components/NotificationToast.vue";

import PreloaderPage from "@/components/PreloaderPage.vue";

const appReady = ref(false);

const icon = useFavicon();
const { identifyUser } = useIdentifyUser();
const authStore = useAuthStore();

onMounted(() => {
  icon.value = "/" + import.meta.env.VITE_APP_NAME.toLowerCase() + ".png";
  loadUserAnalytics();

  const splashTimeout = setTimeout(() => {
    if (!appReady.value) {
      console.warn("[App] Force signaling ready after timeout");
      signalAppReady();

      if (window.posthog) {
        logEvent(trackedEvents.APP_READY_TIMEOUT_EXCEEDED);
      }
    }
  }, 10000);

  // Store timeout ID to clear it later if needed
  window._splashTimeout = splashTimeout;
});

/**
 * Signal to native app that Vue is ready.
 * This tells native to hide the pulsating loader.
 */
const signalAppReady = () => {
  if (appReady.value) return;
  appReady.value = true;

  if (window.SababuuNativeBridge?.webAppMounted) {
    window.SababuuNativeBridge.webAppMounted();
  }

  if (window.SPLASH_PERFORMANCE) {
    window.SPLASH_PERFORMANCE.markAppReady();
  }

  logAppBootTime();
};

const onComponentMounted = () => {
  if (appReady.value) return;

  if (window._splashTimeout) {
    clearTimeout(window._splashTimeout);
  }

  setTimeout(() => {
    signalAppReady();
  }, 100);
};

async function loadUserAnalytics() {
  if (authStore.isAuthenticated) {
    await authStore.updateUserFromApi();
    const user = authStore.getUser();
    identifyUser(user);
  }
}
</script>

<template>
  <div id="wrapper-screen">
    <div class="app-content">
      <!-- Essential notifications only -->
      <div id="wrapper-notification-top">
        <NotificationToast />
      </div>

      <!-- Core app content -->
      <router-view v-slot="{ Component }">
        <div id="wrapper-content">
          <div id="wrapper-component">
            <span id="scroltop-el"></span>
            <component :is="Component" @vue:mounted="onComponentMounted" />
          </div>
        </div>
      </router-view>

      <PreloaderPage />
    </div>
  </div>
</template>

<style lang="scss">
#app {
  width: 100%;
  height: 100%;
  background: #f5f5f5;
  overflow: hidden;
}
</style>

<style scoped lang="scss">
#wrapper-screen {
  max-width: 430px;
  width: 100%;
  height: 100%;
  background: #ffffff;
  margin: 0 auto;
  position: relative;
  overflow-x: hidden;
}

#wrapper-notification-top {
  position: sticky;
  top: 0;
  z-index: 10000;
}

#wrapper-content {
  display: flex;
  flex-direction: column;
  height: 100%;
}

#wrapper-component {
  flex: 1;
  overflow-x: hidden;
  min-height: 0;
}

.app-content {
  height: 100%;
  width: 100%;
}
</style>
